/**
 * AI-403 (docs/ai-business-agent-architecture.md Phase 4) — the typed
 * trigger/action catalog for business automations.
 *
 * The catalog used to be hardcoded in two places (`ACTION_TYPES` /
 * `TRIGGER_TYPES` in `lib/server/automations.ts` and a second copy in
 * `lib/ai/business-agent/workflow-schema.ts`), which is how a new action could
 * be added to one file and silently rejected by the other. This module is the
 * single source of truth, read from `configs/ai/automations.json`.
 *
 * Enforcement is by ALLOW-LIST. A trigger/action that is not in the config is
 * unknown, and unknown is denied — the same safe-by-default rule the guest
 * assistant tool list uses. `sanitizeTrigger` / `sanitizeActions` drop unknown
 * types, and `assertAutomationEntitlement` refuses to save an automation whose
 * trigger/actions are not eligible for the business's real billing state.
 *
 * This module is deliberately pure — no server imports, no DB access — so it
 * can be imported from client code (the builder) and unit-tested in isolation.
 * The eligibility calculation mirrors `lib/ai/business-agent/eligibility.ts`:
 * every gate fails closed with an explicit reason.
 */

import automationsConfig from '@/configs/ai/automations.json';

import { getPlan } from './billing';

// ─── Catalog types ──────────────────────────────────────────────────────────

export interface AutomationCatalogParamProperty {
  type: string;
  enum?: string[];
  description?: string;
}

export interface AutomationCatalogParams {
  properties: Record<string, AutomationCatalogParamProperty>;
  required: string[];
}

export interface AutomationCatalogTrigger {
  id: string;
  label: string;
  description: string;
  /** True only when a dispatcher exists. `schedule` is the only wired trigger. */
  dispatchable: boolean;
  minPlan: string;
  featureKeys: string[];
  channels: string[];
  dataClass: string;
  params: AutomationCatalogParams;
}

export interface AutomationCatalogAction {
  id: string;
  label: string;
  description: string;
  /** The executable AI tool this action maps to (see workflow-runner). */
  tool: string;
  autoExecutable: boolean;
  minPlan: string;
  featureKeys: string[];
  channels: string[];
  dataClass: string;
  params: AutomationCatalogParams;
}

export interface AutomationCatalog {
  triggers: AutomationCatalogTrigger[];
  actions: AutomationCatalogAction[];
}

/**
 * The eligibility input, derived server-side from the business's real billing
 * state (never accepted from a client body). A minimal mirror of
 * `ToolEligibilityInput` from `lib/ai/business-agent/eligibility.ts` — just
 * enough for the catalog gates.
 */
export interface AutomationCatalogEligibilityInput {
  planCode: string;
  enabledFeatures: string[];
  channel: string;
  providerPolicy: 'menuraq_managed' | 'business_owned';
  /** Run-level data class, kept for shape parity with ToolEligibilityInput. */
  dataClass?: string;
}

export interface AutomationCatalogVerdict {
  /** Passes the entitlement gates (plan, feature, channel, data class). */
  eligible: boolean;
  /** Why not — one entry per failed gate. Empty when eligible. */
  reasons: string[];
  /**
   * Whether the item is wired up: `dispatchable` for triggers, always true for
   * actions. Distinct from `eligible` so the builder can show "coming soon"
   * (available false) separately from "upgrade" (eligible false).
   */
  available: boolean;
}

const catalog = automationsConfig as unknown as AutomationCatalog;

// ─── Lookup ─────────────────────────────────────────────────────────────────

export function getAutomationCatalog(): AutomationCatalog {
  return catalog;
}

export function getAutomationTrigger(id: string): AutomationCatalogTrigger | undefined {
  return catalog.triggers.find((t) => t.id === id);
}

export function getAutomationAction(id: string): AutomationCatalogAction | undefined {
  return catalog.actions.find((a) => a.id === id);
}

/** Config allow-list: unknown triggers are denied. */
export function isAutomationTriggerAllowed(id: unknown): boolean {
  return typeof id === 'string' && getAutomationTrigger(id) !== undefined;
}

/** Config allow-list: unknown actions are denied. */
export function isAutomationActionAllowed(id: unknown): boolean {
  return typeof id === 'string' && getAutomationAction(id) !== undefined;
}

// ─── Eligibility ────────────────────────────────────────────────────────────

/** Plan tier order, read from the config `rank` (mirrors getPlanRank). */
function planRank(planCode: string): number {
  return getPlan(planCode)?.rank ?? 0;
}

function planLabel(planCode: string): string {
  return getPlan(planCode)?.label ?? planCode;
}

/**
 * The single eligibility calculation for a catalog item. Every gate fails
 * closed with an explicit reason (mirroring `isToolEligible`):
 *   1. minPlan — the business's plan rank must be >= the item's minPlan rank;
 *   2. featureKeys — every required feature must be in `enabledFeatures`;
 *   3. channel — the run channel must be in the item's allowed channels;
 *   4. data class — financial/confidential data has no compliant provider
 *      under menuraq_managed keys (fail closed).
 *
 * `eligible` is the AND of the four ENTITLEMENT gates only. `available` is a
 * separate signal — false for triggers with no dispatcher yet — so the builder
 * can show "coming soon" (eligible && !available) distinctly from "upgrade"
 * (!eligible). The save-time gate (`assertAutomationEntitlement`) rejects
 * unwired triggers explicitly; folding availability into `eligible` here would
 * make the "coming soon" state unreachable.
 */
export function isAutomationItemEligible(
  item: AutomationCatalogTrigger | AutomationCatalogAction,
  input: AutomationCatalogEligibilityInput,
): AutomationCatalogVerdict {
  const reasons: string[] = [];

  // 1. Plan gate — minPlan via config rank.
  if (planRank(input.planCode) < planRank(item.minPlan)) {
    reasons.push(`requires the ${planLabel(item.minPlan)} plan`);
  }

  // 2. Feature gate.
  for (const feature of item.featureKeys) {
    if (!input.enabledFeatures.includes(feature)) {
      reasons.push(`feature ${feature} not enabled`);
    }
  }

  // 3. Channel gate.
  if (!item.channels.includes(input.channel)) {
    reasons.push(`channel ${input.channel} not allowed`);
  }

  // 4. Data-class gate — financial/confidential data has no compliant provider
  //    under menuraq_managed keys (mirror eligibility.ts:279-285).
  const dataClass = item.dataClass ?? 'operational';
  if (
    (dataClass === 'financial' || dataClass === 'confidential') &&
    input.providerPolicy === 'menuraq_managed'
  ) {
    reasons.push(`data class ${dataClass} has no compliant provider under menuraq_managed`);
  }

  // Availability — triggers without a dispatcher are catalog-level "coming
  // soon", not an entitlement "upgrade". Kept OUT of `eligible`/`reasons` so
  // the builder can render the two states separately.
  const isTrigger = 'dispatchable' in item;
  const available = isTrigger ? item.dispatchable === true : true;

  return { eligible: reasons.length === 0, reasons, available };
}

/**
 * The full catalog resolved against a business's eligibility input. Every item
 * carries its verdict (`eligible` / `reasons` / `available`) so the builder can
 * render each trigger/action with its exact state.
 */
export function resolveAutomationCatalog(input: AutomationCatalogEligibilityInput): {
  triggers: Array<AutomationCatalogTrigger & AutomationCatalogVerdict>;
  actions: Array<AutomationCatalogAction & AutomationCatalogVerdict>;
} {
  return {
    triggers: catalog.triggers.map((trigger) => ({
      ...trigger,
      ...isAutomationItemEligible(trigger, input),
    })),
    actions: catalog.actions.map((action) => ({
      ...action,
      ...isAutomationItemEligible(action, input),
    })),
  };
}